import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftRight } from 'lucide-react';

export const BeforeAfterSlider = () => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  return (
    <section id="gallery" className="py-12 md:py-20 lg:py-32 bg-white" data-testid="gallery-section">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-teal font-semibold text-sm uppercase tracking-wider">Our Work</span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-navy mt-3 mb-4">
            See the Difference
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Drag the slider to see the transformation. Our professional cleaning makes properties inspection-ready.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div
            ref={containerRef}
            className="relative aspect-[4/3] sm:aspect-[16/10] rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)] cursor-ew-resize select-none touch-none"
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => setIsDragging(false)}
            onMouseLeave={() => setIsDragging(false)}
            onMouseMove={handleMouseMove}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => setIsDragging(false)}
            onTouchMove={handleTouchMove}
            data-testid="before-after-slider"
          >
            {/* After Image (Background) */}
            <div className="absolute inset-0">
              <img
                src="/images/kitchen-after.jpg"
                alt="After cleaning - spotless kitchen"
                className="w-full h-full object-cover"
                draggable={false}
              />
              <div className="absolute bottom-4 right-4 bg-teal text-navy px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                AFTER
              </div>
            </div>

            {/* Before Image (Clipped) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src="/images/kitchen-before.jpg"
                alt="Before cleaning - messy kitchen"
                className="w-full h-full object-cover"
                draggable={false}
              />
              <div className="absolute bottom-4 left-4 bg-navy text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                BEFORE
              </div>
            </div>

            {/* Slider Handle */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
              style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-navy">
                <ArrowLeftRight className="w-5 h-5 text-navy" />
              </div>
            </div>
          </div>

          <p className="text-center text-slate-500 mt-6 text-sm">
            Drag or tap the slider to compare before &amp; after
          </p>
        </motion.div>
      </div>
    </section>
  );
};
